
<template>
  <div id="news">
    <b-container class="NewsPage">
      <!-- Search and month filter -->
      <b-row>
        <b-col>
          <div class="searchBlock">
            <b-form-input
              v-model="text"
              @keyup.enter="onEnter"
              placeholder="Search"
            ></b-form-input>

            <b-button
              @click="onEnter"
              class="sIcon"
            >
              <span class="searchBtn BTNSEARCH">Search</span>
            </b-button>
          </div>
        </b-col>

        <b-col>
          <b-form-select
            v-model="selected"
            :options="options"
            @change="getSelectedItem"
            class="monthSelect"
          ></b-form-select>
        </b-col>
      </b-row>

      <!-- Loading indicator -->
      <div v-if="showLoader" class="loadingContainer">
        <div class="loader"></div>
        <div class="loadingText">Loading Articles...</div>
      </div>

      <!-- Error message -->
      <b-alert
        v-if="errorMessage && !showLoader"
        show
        variant="warning"
      >
        {{ errorMessage }}
        <b-button
          size="sm"
          variant="outline-secondary"
          class="ml-2"
          @click="getNews"
        >
          Try again
        </b-button>
      </b-alert>

      <!-- News articles -->
      <b-row v-if="!showLoader && displayedPosts.length">
        <b-col
          v-for="(postCard, index) in displayedPosts"
          :key="postCard.id"
          class="col-lg-4 col-md-6 col-12"
        >
          <b-link
            :to="'/blog/' + postCard.slug"
            class="noUnderline"
          >
            <b-card no-body class="mb-2">
              <img
                :src="getPostImage(postCard)"
                :alt="getPostTitle(postCard)"
                class="card-img-top"
                :loading="index === 0 ? 'eager' : 'lazy'"
                decoding="async"
                width="600"
                height="350"
              />

              <b-card-body>
                <h5
                  class="pt"
                  v-html="getPostTitle(postCard)"
                ></h5>
              </b-card-body>
            </b-card>
          </b-link>
        </b-col>
      </b-row>

      <!-- No articles -->
      <b-row
        v-if="!showLoader && !errorMessage && displayedPosts.length === 0"
      >
        <b-col>
          <p>No articles found.</p>
        </b-col>
      </b-row>

      <!-- Pagination -->
      <nav
        v-if="pages.length > 1 && !showLoader"
        aria-label="News pagination"
      >
        <ul class="pagination">
          <li class="page-item" v-if="paginationGroup > 0">
            <button
              type="button"
              class="page-link"
              @click="goToPreviousGroup"
            >
              Previous 10
            </button>
          </li>

          <li
            v-for="pageNumber in visiblePages"
            :key="pageNumber"
            class="page-item"
            :class="{ active: page === pageNumber }"
          >
            <button
              type="button"
              class="page-link"
              @click="goToPage(pageNumber)"
            >
              {{ pageNumber }}
            </button>
          </li>

          <li
            class="page-item"
            v-if="paginationGroup < totalGroups - 1"
          >
            <button
              type="button"
              class="page-link"
              @click="goToNextGroup"
            >
              Next 10
            </button>
          </li>
        </ul>
      </nav>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "posts",

  data() {
    return {
      posts: [],
      page: 1,
      perPage: 6,

      selected: "Select Month",
      options: [],

      monthStart: null,
      monthEnd: null,

      text: "",
      pages: [],

      showLoader: false,
      errorMessage: "",

      // Prevent older requests overwriting newer results.
      newsRequestId: 0,
      paginationGroup: 0,
    };
  },

  computed: {
    // Keep your existing computed properties as well

    totalGroups() {
      return Math.ceil(this.pages.length / 10);
    },

    visiblePages() {
      const start = this.paginationGroup * 10 + 1;
      const end = Math.min(start + 9, this.pages.length);

      const pageNumbers = [];

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      return pageNumbers;
    },
    setDev() {
      return process.env.VUE_APP_WPAPI_URL + "wp/v2/posts";
    },

    displayedPosts() {
      return this.posts;
    },

  },

  methods: {
    goToNextGroup() {
      if (this.paginationGroup < this.totalGroups - 1) {
        this.paginationGroup++;
        this.page = this.paginationGroup * 10 + 1;
      }
    },

    goToPreviousGroup() {
      if (this.paginationGroup > 0) {
        this.paginationGroup--;
        this.page = this.paginationGroup * 10 + 1;
      }
    },

    goToPage(pageNumber) {
      if (pageNumber !== this.page) {
        this.page = pageNumber;
      }
    },
    onEnter() {
      const searchText = this.text.trim();

      if (!searchText) {
        return;
      }

      window.location.href =
        "/search?search=" + encodeURIComponent(searchText);
    },

    // Generate month dropdown options.
    createMonths() {
      const start = moment("02.2002", "MM.YYYY");
      const end = moment().startOf("month");

      const months = [];
      const current = start.clone();

      while (current.isSameOrBefore(end, "month")) {
        months.push(current.format("MMMM YYYY"));
        current.add(1, "month");
      }

      this.options = [
        "Select Month",
        ...months.reverse()
      ];
    },

    // Month filter
    getSelectedItem() {
      if (this.selected === "Select Month") {
        this.monthStart = null;
        this.monthEnd = null;
      } else {
        const month = moment(this.selected, "MMMM YYYY");

        if (!month.isValid()) {
          return;
        }

        this.monthStart = month
          .clone()
          .startOf("month")
          .toISOString();

        this.monthEnd = month
          .clone()
          .endOf("month")
          .toISOString();
      }

      if (this.page === 1) {
        this.getNews();
      } else {
        this.page = 1;
      }
    },

    // Responsive pagination
    isMobile() {
      return window.innerWidth <= 760;
    },

    // Fetch news from WordPress.
    async getNews() {
      const requestId = ++this.newsRequestId;

      this.showLoader = true;
      this.errorMessage = "";

      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
          _embed: true
        };

        if (this.monthStart && this.monthEnd) {
          params.after = this.monthStart;
          params.before = this.monthEnd;
        }

        const response = await axios.get(this.setDev, {
          params
        });

        // Ignore outdated responses.
        if (requestId !== this.newsRequestId) {
          return;
        }

        this.posts = response.data;

        const totalPages = Math.max(
          1,
          Number(response.headers["x-wp-totalpages"]) || 1
        );

        this.pages = Array.from(
          { length: totalPages },
          (_, index) => index + 1
        );

        // Handle a page that no longer exists after filtering.
        if (this.page > totalPages) {
          this.page = totalPages;
        }
      } catch (error) {
        if (requestId !== this.newsRequestId) {
          return;
        }

        console.error("Failed to load news:", error);

        this.errorMessage =
          "Unable to load articles. Please try again.";

        this.posts = [];
        this.pages = [];
      } finally {
        if (requestId === this.newsRequestId) {
          this.showLoader = false;
        }
      }
    },

    // Featured image or original fallback logo.
    getPostImage(post) {
      const image =
        post.better_featured_image &&
        post.better_featured_image.source_url;

      return (
        image ||
        "https://ocs-sport.ams3.cdn.digitaloceanspaces.com/let_new/2020/11/largelogo-newsIndiviudel-1-1.png"
      );
    },

    // Handle missing post titles.
    getPostTitle(post) {
      if (!post.title || !post.title.rendered) {
        return "";
      }

      return post.title.rendered;
    }
  },

  watch: {
    page(newPage, oldPage) {
      this.paginationGroup = Math.floor((newPage - 1) / 10);

      if (newPage !== oldPage) {
        this.getNews();
      }
    }
  },

  created() {
    this.createMonths();
    this.getNews();
  }
};
</script>

<style scoped>
/* Loading */
.loadingContainer {
  text-align: center;
  padding: 20px 0;
}

.loadingText {
  font-size: 18pt;
  margin-top: 15px;
  margin-bottom: 20px;
  color: #95c93d;
}

.loader {
  border: 12px solid #f3f3f3;
  border-top: 12px solid #95c93d;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  animation: spin 1s linear infinite;
  margin: 10px auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Buttons and colours */
::v-deep .btn-secondary {
  color: #fff;
  background-color: #95c93d;
  border-color: #95c93d;
}

a.btn.sIcon.btn-secondary {
  background: #95c93d;
}

span.searchBtn {
  background-color: #95c93d;
}

.noUnderline {
  text-decoration: none;
}

/* Pagination */
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  padding-left: 0;
  margin-bottom: 1rem;
  list-style: none;
  max-width: 100%;
}

li.page-item {
  display: flex;
  margin-top: 10px;
}

::v-deep .page-link {
  position: relative;
  display: block;
  padding: 0.4rem 0.65rem;
  margin-left: 0;
  line-height: 1.25;
  color: #95c93d;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13pt;
  white-space: nowrap;
}

button.page-link.pagenation.activepage {
  background: #95c93d;
  color: #fff;
}

button.page-link.pagenation:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Month dropdown */
.monthSelect {
  display: inline-block;
  width: 283px;
  float: right;
  height: 37px;
  padding: 0.375rem 1.75rem 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #fff;
  vertical-align: middle;
  appearance: none;
  background-color: #95c93d;
  background-image: url(
    "https://ocs-sport.ams3.cdn.digitaloceanspaces.com/let_new/2023/10/DownArrow.png"
  );
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 12px 7px;
  border: none;
  border-radius: 0;
}

/* Search */
.searchBlock {
  display: inline-flex;
  padding-bottom: 2em;
}

.form-control {
  display: block;
  width: 283px;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d2d4d6;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
}

/* Page layout */
.NewsPage {
  margin-top: 2em;
}

#news {
  background-color: #fff;
  padding-bottom: 5%;
}

.more {
  text-align: center;
}

.show {
  padding-bottom: 2%;
}

.logo {
  text-align: center;
  display: block;
  margin: auto;
  float: right;
  width: 120px;
  margin-top: 35px;
  margin-right: 15px;
}

/* Article cards */
.card-img-top {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 12 / 7;
  object-fit: cover;
  min-height: 183px;
}

.card-body {
  padding: 15px;
  padding-left: 0;
  padding-top: 25px;
}

.card-footer {
  border-radius: 0 0 10px 10px !important;
  background-color: #133f7b;
  color: white;
  text-align: center;
}

#title {
  padding-top: 5%;
  padding-bottom: 5%;
}

.card {
  background-color: transparent;
  border: 0;
}

.exre {
  font-size: 12pt;
  color: #b4b4b4;
}

.pt {
  color: #212529;
  font-size: 13pt;
  padding-left: 0;
  font-weight: 400;
}

.pt:hover {
  color: #015699;
  font-size: 13pt;
  padding-left: 0;
  font-weight: 400;
  text-decoration: none;
}

/* Navigation links */
::v-deep .nav-link {
  color: white;
  background-color: #133f7b;
  margin-top: -11%;
}

.space {
  padding-bottom: 2%;
  border-bottom: 1px solid lightgrey;
  padding-top: 2%;
}

.developGreenText {
  color: #76c3bd !important;
}

.developGreen > .card-footer {
  background-color: #76c3bd !important;
}

.developGreen > .card-footer > li > .nav-link {
  background-color: #76c3bd !important;
}

.developGreen > .card-body {
  min-height: 347px;
}

/* Tablet */
@media only screen and (max-width: 768px) {
  ::v-deep .nav-link {
    text-align: center;
    margin-top: -6%;
  }
}

/* Mobile */
@media only screen and (max-width: 480px) {
  .col {
    flex-basis: auto;
  }

  ::v-deep .nav-link {
    text-align: center;
    margin-top: -6%;
  }

  ::v-deep .row {
    padding-bottom: 4%;
  }

  ::v-deep .form-control {
    display: block;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #d2d4d6;
    border-radius: 0;
    margin-right: 10px;
  }

  ::v-deep a.btn.sIcon.btn-secondary {
    background: #01477b;
    border-radius: 0;
    border-color: #01477b;
  }

  ::v-deep .monthSelect {
    width: 100%;
  }

  ::v-deep .searchBlock {
    display: flex;
    padding-bottom: 2em;
  }

  .pagination {
    gap: 2px;
  }

  ::v-deep .page-link {
    padding: 0.35rem 0.5rem;
    font-size: 11pt;
  }
}
</style>
